// ** Redux Imports
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"

// ** Axios Imports
import {
  getWebAboutApi,
  getWebAboutByIdApi,
  addOrUpdateWebAboutApi
} from "../../../../services/Web/weAbout.service"

import { toastError, toastSuccess } from "../../../../utility/toastutill"

// ** Async Thunks
export const fetchWebAbout = createAsyncThunk(
  "webAbout/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getWebAboutApi()
      return response.data
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch about page data"
      toastError(errorMessage)
      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status
      })
    }
  }
)

export const fetchWebAboutById = createAsyncThunk(
  "webAbout/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getWebAboutByIdApi(id)
      return response.data
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch about page data"
      toastError(errorMessage)
      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status
      })
    }
  }
)

export const createOrUpdateWebAbout = createAsyncThunk(
  "webAbout/createOrUpdate",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await addOrUpdateWebAboutApi(formData)
      toastSuccess("About page data saved successfully")
      return response.data
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to save about page data"
      toastError(errorMessage)
      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status
      })
    }
  }
)

// ** Initial State
const initialState = {
  data: null,
  selectedWebAbout: null,
  loading: false,
  error: null,
  total: 0,
  lastUpdated: null
}

// ** Slice
export const webAboutSlice = createSlice({
  name: "webAbout",
  initialState,
  reducers: {
    clearSelectedWebAbout: (state) => {
      state.selectedWebAbout = null
    },
    clearError: (state) => {
      state.error = null
    },
    resetWebAboutState: (state) => {
      state.data = null
      state.selectedWebAbout = null
      state.loading = false
      state.error = null
      state.total = 0
      state.lastUpdated = null
    },
    // In your store/index.js for webAbout
    updateFormData: (state, action) => {
      const updatedData = action.payload

      if (state.selectedWebAbout) {
        state.selectedWebAbout = {
          ...state.selectedWebAbout,
          ...updatedData
        }
      }

      if (state.data) {
        state.data = {
          ...state.data,
          ...updatedData
        }
      }

      state.lastUpdated = new Date().toISOString()
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all about page data
      .addCase(fetchWebAbout.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchWebAbout.fulfilled, (state, action) => {
        state.loading = false
        const responseData = action.payload.data || action.payload

        state.data = responseData
        state.selectedWebAbout = responseData
        state.total = 1
        state.lastUpdated = new Date().toISOString()
        state.error = null
      })
      .addCase(fetchWebAbout.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastUpdated = new Date().toISOString()
      })
      // Fetch about page by ID
      .addCase(fetchWebAboutById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchWebAboutById.fulfilled, (state, action) => {
        state.loading = false
        const responseData = action.payload.data || action.payload
        state.selectedWebAbout = responseData

        if (state.data && state.data._id === responseData._id) {
          state.data = responseData
        }
        state.lastUpdated = new Date().toISOString()
        state.error = null
      })
      .addCase(fetchWebAboutById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastUpdated = new Date().toISOString()
      })
      // Create or update about page
      .addCase(createOrUpdateWebAbout.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createOrUpdateWebAbout.fulfilled, (state, action) => {
        state.loading = false
        const updatedData = action.payload.data || action.payload

        state.data = updatedData
        state.selectedWebAbout = updatedData
        state.total = 1
        state.lastUpdated = new Date().toISOString()
        state.error = null
      })
      .addCase(createOrUpdateWebAbout.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastUpdated = new Date().toISOString()
      })
  }
})

// ** Actions
export const {
  clearSelectedWebAbout,
  clearError,
  resetWebAboutState,
  updateFormData,
  setLoading
} = webAboutSlice.actions

// ** Selectors
export const selectWebAbout = (state) => state.webAbout.data
export const selectSelectedWebAbout = (state) => state.webAbout.selectedWebAbout
export const selectWebAboutLoading = (state) => state.webAbout.loading
export const selectWebAboutError = (state) => state.webAbout.error
export const selectWebAboutLastUpdated = (state) => state.webAbout.lastUpdated

// ** Reducer
export default webAboutSlice.reducer
